import { useState } from "react";
import { toast } from "react-hot-toast";
import { Pencil, Plus, Trash2 } from "lucide-react";

import { Button } from "components/ui/Button";
import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { DataTable } from "components/ui/DataTable";
import { PageHeader } from "components/ui/PageHeader";
import { stripHtml } from "utils/stripHtml";

import { useCreateNoteTemplate } from "../../hooks/useCreateNoteTemplate";
import { useDeleteNoteTemplate } from "../../hooks/useDeleteNoteTemplate";
import { useNoteTemplatesAll } from "../../hooks/useNoteTemplatesAll";
import { NoteTemplateModal } from "../NoteTemplateModal";
import styles from "./ConsultationNoteTemplatesTable.module.css";

const columns = [
  {
    key: "name",
    header: "Name",
    width: "minmax(150px, 1fr)",
    render: (row) => row.name,
  },
  {
    key: "note",
    header: "Template Text",
    width: "950px",
    render: (row) => {
      const plain = stripHtml(row.note);
      return plain ? plain.substring(0, 200) + (plain.length > 200 ? "..." : "") : "—";
    },
  },
];

export const ConsultationNoteTemplatesTable = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data: templates = [], isLoading } = useNoteTemplatesAll();
  const { mutate: saveTemplate } = useCreateNoteTemplate();
  const { mutate: deleteTemplate } = useDeleteNoteTemplate();

  const handleAddTemplate = () => {
    setSelectedTemplate(null);
    setIsModalOpen(true);
  };

  const handleEditTemplate = (row) => {
    setSelectedTemplate(row);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleSubmit = (formValues) => {
    const isEditing = !!selectedTemplate;
    const payload = {
      name: formValues.name,
      note: formValues.note,
      ...(isEditing ? { id: selectedTemplate.id } : {}),
    };

    saveTemplate(payload, {
      onSuccess: () => {
        toast.success(
          isEditing
            ? "Template updated successfully."
            : "Template saved successfully."
        );
        setIsModalOpen(false);
        setSelectedTemplate(null);
      },
      onError: (error) => toast.error(error.message),
    });
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deleteTemplate(deleteTarget.id, {
      onSuccess: () => toast.success("Template deleted successfully."),
      onError: (error) => toast.error(error.message),
    });
    setDeleteTarget(null);
  };

  const rowActions = [
    {
      label: "Edit",
      icon: Pencil,
      primary: true,
      onClick: (row) => handleEditTemplate(row),
    },
    {
      label: "Delete",
      icon: Trash2,
      primary: true,
      onClick: (row) => setDeleteTarget(row),
    },
  ];

  return (
    <div>
      <PageHeader title="Note Templates">
        <Button
          variant="text"
          endIcon={Plus}
          data-testid="add-note-template-button"
          onClick={handleAddTemplate}
        >
          Add Note Template
        </Button>
      </PageHeader>

      <div className={styles.tableWrapper}>
        <DataTable
          columns={columns}
          data={templates}
          isLoading={isLoading}
          emptyTitle="No templates found"
          emptyText="There are no note templates to display."
          rowsPerPage={20}
          rowActions={rowActions}
        />
      </div>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Template"
        message={`Are you sure you want to delete "${deleteTarget?.name}"?`}
        confirmLabel="Delete"
        confirmVariant="primary"
      />

      <NoteTemplateModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleSubmit}
        editTemplate={selectedTemplate}
      />
    </div>
  );
};
